// Authorisation configuration for the account-hold staff tool (BX-7).
//
// ===========================================================================
//  SIGNED OFF — Zach, BX-7 comments 36716, 37482 and 37560.
//  A dedicated `kyc` group — "A1- KYC group makes sense" — NOT `admin`.
//  It is deliberately the ONLY place the group is named in TypeScript.
//  To change who may place or release an account hold, edit this one array.
// ===========================================================================
//
// WHY THE GROUP IS STILL CALLED `kyc` when the actions are no longer KYC
// actions (comment 37560 replaced restrict/clear/grace with the ops account
// hold): it is the same set of people, and renaming a Cognito group is not a
// rename — every member has to be re-enrolled in the new one, with a window in
// which the tool is unusable. The group name is an identifier, not a
// description, so it stays.
//
// Why a dedicated group rather than reusing `admin` or `support`: the approvers
// named on the ticket (Haylee Evans, Robert Beer, Blake Cassidy, Kevin Upton,
// Zach Crellin — comment 36716) are not the same set as any existing group, and
// encoding people as a list here would need a deploy every time someone joined
// or left. A group indirects that into Cognito, where membership belongs.
//
// `admin` is deliberately NOT included. An admin who should be able to hold an
// account goes in the `kyc` group — that keeps this array the single honest
// answer to "who may do this", rather than a partial one with an implicit
// superuser standing behind it.
//
// DEPLOY NOTE — this fails CLOSED, on purpose. The `kyc` group must exist and
// have members before the tool is usable: `custom:groups` is set per user
// (COGNITO_GROUPS.md) and injected into the JWT by the Pre Token Generation
// trigger at LOGIN, so anyone added has to sign out and back in before the
// controls appear. If nobody is in the group, nobody can place or release a
// hold — under-granting is fixed by adding a member, whereas over-granting is a
// control failure.
//
// This lives in libs/schema — NOT in libs/cognito — because it is the one
// value both the browser (apps/web, for UX-only button hiding) and the API
// Lambda (libs/cognito, for the real server-side check) must agree on, and
// both already depend on @bamboard/schema.
//
// LOCKSTEP WARNING: AppSync schema directives cannot import a TypeScript
// constant, so `libs/schema/graphql/schema.graphql` repeats these names in the
// `@aws_cognito_user_pools(cognito_groups: [...])` directive on
// placeAccountHold / releaseAccountHold. `staffActionGroups.lockstep.test.ts`
// parses that file and fails if the two drift, so a one-line change here is
// caught immediately if the schema is not updated to match.
export const STAFF_ACTION_GROUPS: readonly string[] = ['kyc'];

// UX-only helper for the browser: hides controls the caller cannot use. This is
// NOT authorisation — the boundary is the AppSync directive plus the resolver
// check in libs/cognito/src/lib/staffAuthz.ts.
export const mayPerformStaffAction = (
  groups: readonly string[] | null | undefined,
): boolean =>
  !!groups && groups.some((group) => STAFF_ACTION_GROUPS.includes(group));
